import { Badge, BadgeText } from "../../../../../ui/badge";
import { Button, ButtonText } from "../../../../../ui/button";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "../../../../../ui/checkbox";
import { HStack } from "../../../../../ui/hstack";
import { VStack } from "../../../../../ui/vstack";
import { formatSimpleMoney } from "../../../../../utils/money";
import { Check } from "lucide-react-native";
import AmountInput from "./AmountInput";
import { digitsOnly } from "./moneyDigits";
import type { FieldControlProps } from "../fieldTypes";
import { isBarterOnly, moneyAmount, rangeFor } from "../formValues";

/**
 * An amount in the brand's currency, with Montu's reference range beside it.
 *
 * The currency is shown, never chosen — it is the brand's. The range is the one for
 * whatever the `rangeBy` field (the creator type) currently says, so it changes under
 * the person's hands as they change that answer; with no selection there is no range
 * and nothing is shown rather than a wrong one.
 *
 * "Solo canje" replaces the amount rather than adding to it: the two are exclusive on
 * the wire (`{amount}` or `{barterOnly}`), so ticking it empties and disables the box.
 */
const MoneyField = ({
  field,
  value,
  values,
  onChange,
  disabled,
  invalid,
  onBlur,
}: FieldControlProps) => {
  const barter = isBarterOnly(value);
  const amount = moneyAmount(value);
  const range = rangeFor(field, values);
  const money = (raw: string) =>
    formatSimpleMoney({ amount: raw, currency: field.currency });

  return (
    <VStack space="sm">
      <AmountInput
        value={amount}
        currency={field.currency}
        onChange={(digits) => onChange({ amount: digits })}
        onBlur={onBlur}
        disabled={disabled || barter}
        invalid={invalid}
        accessibilityLabel={`${field.label} (${field.currency})`}
      />

      {range && !barter ? (
        <HStack space="sm" className="flex-wrap items-center">
          <Badge action="info" variant="outline" size="md" rounded="md">
            <BadgeText>
              {`Recomendado: ${money(range.min)} – ${money(range.max)}`}
            </BadgeText>
          </Badge>
          {range.recommended ? (
            <Button
              action="brand"
              variant="link"
              size="xs"
              className="min-h-11"
              isDisabled={disabled}
              onPress={() =>
                onChange({ amount: digitsOnly(range.recommended) })
              }
            >
              <ButtonText>{`Usar ${money(range.recommended)}`}</ButtonText>
            </Button>
          ) : null}
        </HStack>
      ) : null}

      {field.allowBarter ? (
        <Checkbox
          size="md"
          action="brand"
          value={`${field.key}-barter`}
          isChecked={barter}
          isDisabled={disabled}
          onChange={(checked) =>
            onChange(checked ? { barterOnly: true } : { amount: "" })
          }
          className="min-h-11"
        >
          <CheckboxIndicator>
            <CheckboxIcon as={Check} />
          </CheckboxIndicator>
          <CheckboxLabel>Solo canje</CheckboxLabel>
        </Checkbox>
      ) : null}
    </VStack>
  );
};

export default MoneyField;
